/**
 * Order History Page for HMH Lunch Ordering System
 *
 * Displays user's past orders with details.
 */

import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "../components/auth";
import { useMyOrders, useCancelOrder, Order } from "../hooks/useOrders";
import { Toast } from "../components/ui";

export function OrderHistoryPage() {
  const { user, logout, accessToken, isSsoUser } = useAuth();
  const { orders, fetchOrders, loading, error } = useMyOrders();
  const { cancelOrder, loading: cancelling, error: cancelError } = useCancelOrder();
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  useEffect(() => {
    if (accessToken) {
      fetchOrders(accessToken);
    }
  }, [accessToken, fetchOrders]);

  const handleCancel = async (orderId: number) => {
    if (!accessToken) return;

    if (confirm("Are you sure you want to cancel this order?")) {
      const success = await cancelOrder(accessToken, orderId);
      if (success) {
        setToastMessage("Order cancelled successfully");
        setShowToast(true);
        // Refresh orders
        fetchOrders(accessToken);
      } else {
        setToastMessage(cancelError || "Failed to cancel order");
        setShowToast(true);
      }
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getOrderTypeBadge = (orderType: string) => {
    const colors = {
      regular: "bg-blue-100 text-blue-800",
      salad: "bg-green-100 text-green-800",
      special: "bg-purple-100 text-purple-800",
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[orderType as keyof typeof colors] || "bg-gray-100 text-gray-800"}`}>
        {orderType.charAt(0).toUpperCase() + orderType.slice(1)}
      </span>
    );
  };

  const getStatusBadge = (status: string) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800",
      confirmed: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800",
      completed: "bg-gray-100 text-gray-800",
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[status as keyof typeof colors] || "bg-gray-100 text-gray-800"}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const canCancelOrder = (order: Order) => {
    // Can only cancel pending orders for today or tomorrow
    if (order.order_status !== "pending") return false;

    const orderDate = new Date(order.order_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    return orderDate >= today && orderDate <= tomorrow;
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Toast Notification */}
      <Toast
        message={toastMessage}
        show={showToast}
        onClose={() => setShowToast(false)}
        duration={4000}
      />

      {/* Header */}
      <header className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <img
                src="https://hmhintuneassets.blob.core.windows.net/iconstorage/hmh-logo.png?sv=2025-07-05&spr=https&st=2025-12-31T19%3A56%3A45Z&se=2065-01-01T19%3A56%3A00Z&sr=b&sp=r&sig=IzQGQtaPVf5DP7%2FPI4yizcpybi9HJm0vQ%2FnJ6dUZ%2BcI%3D"
                alt="Hood Memorial Hospital"
                className="h-12 w-auto"
              />
              <div className="border-l-2 border-gray-300 pl-4">
                <p className="text-hmh-dark font-semibold">Lunch Ordering System</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="font-medium text-gray-900">{user?.fullName}</p>
                <p className="text-sm text-gray-500">
                  {user?.employeeNumber && `#${user.employeeNumber}`}
                  {isSsoUser && " (SSO)"}
                </p>
              </div>
              {user?.isAdmin && (
                <Link
                  to="/admin"
                  className="px-4 py-2 text-sm font-medium text-hmh-blue border border-hmh-blue rounded-lg hover:bg-hmh-blue hover:text-white transition-colors"
                >
                  Admin Console
                </Link>
              )}
              <button
                onClick={logout}
                className="px-4 py-2 text-sm font-medium text-white bg-hmh-blue rounded-lg hover:bg-hmh-dark transition-colors"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-6">
          <Link
            to="/"
            className="inline-flex items-center text-hmh-blue hover:text-hmh-dark font-medium mb-4"
          >
            <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </Link>

          <h1 className="text-3xl font-bold text-hmh-dark">Order History</h1>
          <p className="text-gray-600 mt-2">View all your past orders</p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <svg
              className="animate-spin h-12 w-12 text-hmh-blue mx-auto mb-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <p className="text-gray-600">Loading your orders...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && orders.length === 0 && (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <svg
              className="h-16 w-16 text-gray-400 mx-auto mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No orders yet</h3>
            <p className="text-gray-600 mb-6">You haven't placed any orders. Start by ordering from today's menu.</p>
            <Link
              to="/"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-hmh-blue hover:bg-hmh-dark transition-colors"
            >
              View Menu
            </Link>
          </div>
        )}

        {/* Orders List */}
        {!loading && !error && orders.length > 0 && (
          <div className="space-y-4">
            {orders.map((order: Order) => (
              <div
                key={order.id}
                className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-hmh-dark">
                        {order.order_type === "regular" && order.menu_item_name}
                        {order.order_type === "special" && order.special_name}
                        {order.order_type === "salad" && "Custom Salad"}
                      </h3>
                      {getOrderTypeBadge(order.order_type)}
                      {getStatusBadge(order.order_status)}
                    </div>

                    <p className="text-sm text-gray-600 mb-1">
                      Order Date: <span className="font-medium">{formatDate(order.order_date)}</span>
                    </p>

                    <p className="text-xs text-gray-500">
                      Ordered on: {formatDate(order.created_at)}
                    </p>
                  </div>

                  {canCancelOrder(order) && (
                    <button
                      onClick={() => handleCancel(order.id)}
                      disabled={cancelling}
                      className="px-4 py-2 text-sm font-medium text-red-600 border border-red-600 rounded-lg hover:bg-red-600 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {cancelling ? "Cancelling..." : "Cancel Order"}
                    </button>
                  )}
                </div>

                {/* Order Details */}
                <div className="border-t pt-4">
                  {order.order_type === "regular" && order.menu_item_description && (
                    <p className="text-gray-700 mb-2">{order.menu_item_description}</p>
                  )}

                  {order.order_type === "special" && order.special_description && (
                    <p className="text-gray-700 mb-2">{order.special_description}</p>
                  )}

                  {order.order_type === "salad" && order.toppings && order.toppings.length > 0 && (
                    <div className="mb-2">
                      <p className="text-sm font-medium text-gray-700 mb-2">Toppings:</p>
                      <div className="flex flex-wrap gap-2">
                        {order.toppings.map((topping) => (
                          <span
                            key={topping.id}
                            className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700"
                          >
                            {topping.topping_name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {order.notes && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm font-medium text-gray-700 mb-1">Notes:</p>
                      <p className="text-sm text-gray-600">{order.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-auto">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8 text-center text-sm text-gray-500">
          Hood Memorial Hospital - Amite, Louisiana
        </div>
      </footer>
    </div>
  );
}
